import { useUpdateSuperAdminShopAlerts } from "@babackapp/private-api-client/client/use-super-admin-shop-configuration";
import type { SuperAdminShopAlertDto } from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Switch } from "@babackapp/ui/components/switch";
import { useState } from "react";
import { ConfigurationSection } from "./shop-account-sections";

/**
 * Slack alerts about this shop, each on unless turned off here (a test shop,
 * a failure already followed). A muted alert stays in the journal, with the
 * "Coupée" outcome: only the Slack post is skipped.
 */
export function ShopAlertsSection({
  shopId,
  alerts,
}: {
  shopId: string;
  alerts: SuperAdminShopAlertDto[];
}) {
  const update = useUpdateSuperAdminShopAlerts(shopId);
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const changes = Object.fromEntries(
    Object.entries(draft).filter(
      ([kind, isEnabled]) =>
        alerts.find((alert) => alert.kind === kind)?.isEnabled !== isEnabled,
    ),
  );
  const changedCount = Object.keys(changes).length;
  const mutedCount = alerts.filter((alert) => !alert.isEnabled).length;
  const channels = [...new Set(alerts.map((alert) => alert.channel))];

  function save() {
    update.mutate(
      { params: { path: { shopId } }, body: { changes } },
      { onSuccess: () => setDraft({}) },
    );
  }

  return (
    <ConfigurationSection
      title="Alertes Slack"
      description={`Alertes #monitor-* concernant cette boutique, toutes actives par défaut. Une alerte coupée n'est plus postée sur Slack mais reste dans le journal. Les alertes de sécurité ne se coupent pas.${
        mutedCount > 0
          ? ` ${mutedCount} coupée${mutedCount > 1 ? "s" : ""}.`
          : ""
      }`}
    >
      <div className="divide-y">
        {channels.map((channel) => (
          <div key={channel} className="px-5 py-3">
            <h3 className="mb-2 font-mono text-xs font-semibold text-muted-foreground">
              #monitor-{channel}
            </h3>
            <div className="divide-y">
              {alerts
                .filter((alert) => alert.channel === channel)
                .map((alert) => {
                  const isEnabled = draft[alert.kind] ?? alert.isEnabled;
                  return (
                    <label
                      key={alert.kind}
                      className="flex cursor-pointer items-center justify-between gap-6 py-2"
                    >
                      <span className="text-sm text-foreground">
                        {alert.label}
                        {!isEnabled && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            coupée
                          </span>
                        )}
                        {alert.kind in changes && (
                          <span className="ml-2 text-xs font-normal text-violet-700">
                            modifié
                          </span>
                        )}
                      </span>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) =>
                          setDraft((current) => ({
                            ...current,
                            [alert.kind]: checked,
                          }))
                        }
                      />
                    </label>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-3 border-t px-5 py-3">
        {update.isError && (
          <span className="text-xs text-destructive">
            Enregistrement refusé.
          </span>
        )}
        {changedCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setDraft({})}>
            Annuler
          </Button>
        )}
        <Button
          size="sm"
          disabled={changedCount === 0 || update.isPending}
          onClick={save}
        >
          {update.isPending
            ? "Enregistrement…"
            : changedCount > 0
              ? `Enregistrer ${changedCount} alerte${changedCount > 1 ? "s" : ""}`
              : "Enregistrer"}
        </Button>
      </div>
    </ConfigurationSection>
  );
}
